import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SaudiRiyalIcon } from '../../ui';
import SectionCard from './SectionCard';
import KpiStat from './KpiStat';
import StatusBar, { statusBarItems } from './StatusBar';
import useModuleStats from './useModuleStats';
import RecentItems from './RecentItems';
import { paymentStatusLabels, formatMoney } from './statusLabels';
import { formatCount } from '../overview/format';

/** Payments — collected vs total amounts + verification queue + status mix. */
const PaymentsSection: React.FC = () => {
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const { data, loading, error } = useModuleStats<any>('/payments/statistics/');

  if (error) return null;
  // No records yet: keep the module visible with an empty state rather than hiding it.
  const empty = !loading && (!data || (data.total ?? 0) === 0)
    ? {
        title: isRTL ? 'لا توجد مدفوعات حتى الآن' : 'No payments yet',
        text: isRTL ? 'ستظهر هنا المبالغ المحصّلة وحالة الدفعات بمجرد تسجيل أول دفعة.' : 'Collected amounts and payment status will appear here once the first payment is recorded.',
      }
    : null;

  const byStatus: Record<string, number> = {
    pending: data?.pending,
    initiated: data?.initiated,
    captured: data?.captured,
    failed: data?.failed,
    refunded: data?.refunded,
    cancelled: data?.cancelled,
  };

  const pendingVerification = data?.pending_verification ?? 0;
  const canVerify = user?.can_verify_payments;

  const money = (v: any) => (
    <span className="inline-flex items-center gap-1">
      {formatMoney(v ?? 0, isRTL)}
      <SaudiRiyalIcon className="w-4 h-4 opacity-70" />
    </span>
  );

  return (
    <SectionCard
      icon="bi-credit-card"
      title={isRTL ? 'المدفوعات' : 'Payments'}
      caption={isRTL ? 'المحصّل وحالة الدفعات' : 'Collected amounts and status'}
      to="/payments"
      loading={loading}
      empty={empty}
      stats={(
        <>
          <KpiStat value={formatCount(data?.total)} label={isRTL ? 'الإجمالي' : 'Total'} />
          <KpiStat value={money(data?.captured_amount)} label={isRTL ? 'المُحصّل' : 'Collected'} tone="success" />
          <KpiStat value={money(data?.total_amount)} label={isRTL ? 'إجمالي المبالغ' : 'Total Amount'} />
          <KpiStat
            value={formatCount(pendingVerification)}
            label={isRTL ? 'بانتظار التحقق' : 'Pending Verification'}
            tone={pendingVerification > 0 ? 'warning' : 'default'}
          />
        </>
      )}
    >
      {pendingVerification > 0 && canVerify && (
        <Link to="/payments/pending-verification" className="dash-card-link inline-flex items-center gap-2">
          <i className="bi bi-exclamation-circle" aria-hidden="true"></i>
          {isRTL
            ? `${pendingVerification} دفعة بحاجة إلى تحقق — انتقل إلى قائمة التحقق`
            : `${pendingVerification} payment(s) need verification — open the queue`}
        </Link>
      )}
      <StatusBar items={statusBarItems(byStatus, paymentStatusLabels(isRTL))} />
      <RecentItems
        endpoint="/payments/"
        title={isRTL ? 'أحدث المدفوعات' : 'Recent Payments'}
        itemLink={(p) => `/payments/${p.id}`}
        primary={(p) => (isRTL && p.description_ar ? p.description_ar : p.description) || p.payment_id}
        secondary={(p) => p.payment_id}
        statusKey={(p) => p.status}
        labels={paymentStatusLabels(isRTL)}
      />
    </SectionCard>
  );
};

export default PaymentsSection;
